import {
  ADMIN_NOTE_MAX,
  type GetUsersResponse,
} from "@mg/schemas/src/christo/catalyst";
import { CheckCircle } from "@phosphor-icons/react";
import { Button, Dialog, Flex, Text, TextArea } from "@radix-ui/themes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useUI } from "../../../contexts/ui";
import { errorAnalyticsPayload, useAnalytics } from "../../../utils/analytics";
import { errorMessage } from "../../../utils/http";
import { useUserAdminNoteMutation } from "../../../utils/queries/users";

type NoteUser = Pick<
  GetUsersResponse[number],
  "_id" | "name" | "role" | "adminNote"
>;

interface UserAdminNoteDialogProps {
  /** The Manage Users row being annotated; null keeps the dialog closed. */
  user: NoteUser | null;
  onClose: () => void;
  /** Fires after christo confirms the write so the caller can refetch. */
  onSaved: () => void;
}

/**
 * Manage Users > admin note about a user.
 *
 * Whoever manages users writes a short note ABOUT a user. christo stores it on
 * the user's enterprise profile (`enterpriseProfiles[].adminNote`) for
 * downstream AI use. Access follows the Manage Users gate end to end: the
 * trigger in usersRoute renders wherever the actions column does, the endpoint
 * authorizes like the other Manage Users actions, and christo lifts the note
 * into user lists only for callers who pass that same gate.
 */
export function UserAdminNoteDialog({
  user,
  onClose,
  onSaved,
}: UserAdminNoteDialogProps) {
  const { t } = useTranslation("ui");
  const { notify } = useUI();
  const posthog = useAnalytics("Users");
  const adminNoteMutation = useUserAdminNoteMutation();
  const [draft, setDraft] = useState("");

  // Re-seed whenever a different row opens; no note reads as an empty draft.
  useEffect(() => {
    setDraft(user?.adminNote ?? "");
  }, [user?._id, user?.adminNote]);

  const name = user?.name ?? "";
  const title = t("settings.usersRoute.adminNoteTitle", { name });

  const save = () => {
    if (!user) return;
    const target = user;
    // Trimmed on the way out so a whitespace-only edit clears the note
    // rather than storing blanks.
    const adminNote = draft.trim();
    adminNoteMutation.mutate(
      { userId: target._id, adminNote },
      {
        onSuccess: () => {
          posthog.capture("user_admin_note_updated", {
            userId: target._id,
            userRole: target.role,
            noteLength: adminNote.length,
            cleared: adminNote.length === 0,
          });
          notify({
            variant: "success",
            title: "Admin note saved",
            message: `Saved on ${target.name ?? "this user"}'s profile.`,
            leadingIcon: <CheckCircle color="rgb(var(--base-black))" />,
          });
          onSaved();
          onClose();
        },
        // async: errorMessage unwraps a Response body, which is how christo's
        // 403/400 reason reaches the toast.
        onError: async (error) => {
          posthog.capture("user_admin_note_updated_error", {
            userId: target._id,
            userRole: target.role,
            ...errorAnalyticsPayload(error),
          });
          notify({
            variant: "error",
            title: "Failed to save admin note",
            message: await errorMessage(error),
          });
        },
      },
    );
  };

  return (
    <Dialog.Root
      open={user != null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <Dialog.Content
        className="grid w-[90vw] max-w-lg gap-4"
        data-testid="user-admin-note-dialog"
      >
        <Dialog.Title data-testid="user-admin-note-dialog-title">
          {title}
        </Dialog.Title>
        {/* why-not: no explicit id here. Radix wires its generated id into
            Dialog.Content's aria-describedby; overriding it leaves that IDREF
            dangling. */}
        <Dialog.Description size="2" color="gray">
          {t("settings.usersRoute.adminNoteHint")}
        </Dialog.Description>
        <TextArea
          value={draft}
          onChange={(event) => setDraft(event.currentTarget.value)}
          maxLength={ADMIN_NOTE_MAX}
          rows={4}
          placeholder={t("settings.usersRoute.adminNotePlaceholder")}
          aria-label={t("settings.usersRoute.adminNoteLabel")}
          data-testid="user-admin-note-input"
        />
        <Text size="1" color="gray" data-testid="user-admin-note-counter">
          {draft.length}/{ADMIN_NOTE_MAX}
        </Text>
        <Flex gap="2" justify="end">
          <Dialog.Close>
            <Button variant="outline" data-testid="user-admin-note-cancel">
              {t("cancel")}
            </Button>
          </Dialog.Close>
          <Button
            disabled={adminNoteMutation.isPending || user == null}
            data-testid="user-admin-note-save"
            onClick={save}
          >
            {t("save")}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
